// SchedulePlanModal: the Production calendar's schedule plan (Script list, Ready tab), migrated (DESIGN rev 2.1).
// The Production page's Shoot stage uses it in card mode too (HD-8 retired SchedulePlanModalLegacy.jsx).
// Two entry points, unchanged:
//   Ready tab:   <SchedulePlanModal scriptId=… />                          → POST /api/tal/:scriptId/confirm
//   Production:  <SchedulePlanModal card={{ id, title, clientName, scriptId, segments }} /> → PUT /api/shoot-segments/:id/plan
// Same reads: GET /api/talent, GET /api/tal/:scriptId/suggest, GET /api/talent-availability/by-date,
// GET /api/talent-availability/by-range (one talent's month), GET /api/talent-availability/date-counts (the big picker),
// POST /api/tal/:scriptId/ensure-live-production (a script with no production yet).
// In card mode the rows seed from the card's UNFILMED segments when it has any (edit), otherwise from the script's
// suggestion (first plan); filmed segments are listed read-only and the server leaves them alone. Cards without a
// script (Riff) or without characters plan talent-only rows.
import { useState, useEffect, useMemo, useRef } from 'react';
import { CalendarDays, Check, ChevronLeft, ChevronRight, Plus, TriangleAlert, X } from 'lucide-react';
import api from '../lib/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import CalendarCell from '../ui/CalendarCell';
import DatePicker from '../ui/DatePicker';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import { clean } from '../shell/copy';
import Modal from '../ui/Modal';
import { DAY_NAMES, MONTH_NAMES, buildCalDays, dateKey } from '../pages/calendar/util';

const DOW_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// One talent's available days in a compact month, shown under a row whose talent is not available on the chosen
// day: click an available day to move the shoot there.
export function TalentAvailabilityMini({ talentId, talentName, baseDate, onPick }) {
  const init = baseDate ? new Date(`${baseDate}T00:00:00`) : new Date();
  const [year,       setYear]       = useState(init.getFullYear());
  const [month,      setMonth]      = useState(init.getMonth());
  const [availDates, setAvailDates] = useState(new Set());
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!talentId) return undefined;
    const mm = String(month + 1).padStart(2, '0');
    const first = `${year}-${mm}-01`;
    const last  = `${year}-${mm}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
    let cancelled = false;
    setLoading(true);
    api.get(`/api/talent-availability/by-range?from=${first}&to=${last}`)
      .then(r => {
        if (cancelled) return;
        const dates = new Set();
        for (const [d, rows] of Object.entries(r.data || {})) if (rows.some(rr => rr.talentId === talentId)) dates.add(d);
        setAvailDates(dates);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [talentId, year, month]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const days = buildCalDays(year, month);
  const who = clean(talentName) || 'This talent';

  return (
    <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={prevMonth} />
        <span className="ui-meta" aria-live="polite">{who} available, {MONTH_NAMES[month]} {year}{loading ? ' (loading)' : ''}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={nextMonth} />
      </div>
      <div className="ui-month ui-month--compact">
        {DOW_SHORT.map((d, i) => <div key={i} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {days.map(d => {
          const k = dateKey(d);
          if (d.getMonth() !== month) return <CalendarCell key={k} date={k} outside />;
          const avail = availDates.has(k);
          return <CalendarCell key={k} date={k} data-tone={avail ? 'warn' : undefined} disabled={!avail} onClick={() => avail && onPick(k)} />;
        })}
      </div>
      {!loading && availDates.size === 0 && <p className="ui-p ui-meta">No availability this month.</p>}
    </div>
  );
}

// A full month in its own dialog so a shoot day can be picked by eye; each day shows how many talent are available.
export function BigCalendarPickerModal({ value, title, onPick, onClose }) {
  const init = value ? new Date(`${value}T00:00:00`) : new Date();
  const [year,   setYear]   = useState(init.getFullYear());
  const [month,  setMonth]  = useState(init.getMonth());
  const [counts, setCounts] = useState({});
  const days     = useMemo(() => buildCalDays(year, month), [year, month]);
  const todayKey = dateKey(new Date());

  useEffect(() => {
    const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const last = new Date(year, month + 1, 0);
    let cancelled = false;
    api.get(`/api/talent-availability/date-counts?from=${from}&to=${dateKey(last)}`)
      .then(r => { if (!cancelled) setCounts(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [year, month]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  return (
    <Modal size="wide" title={title || 'Pick a shoot day'} onClose={onClose}>
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={prevMonth} />
        <span className="ui-heading" aria-live="polite">{MONTH_NAMES[month]} {year}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={nextMonth} />
      </div>
      <div className="ui-month">
        {DAY_NAMES.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {days.map(day => {
          const key = dateKey(day);
          if (day.getMonth() !== month) return <CalendarCell key={key} date={key} outside />;
          const count = counts[key] || 0;
          return (
            <CalendarCell key={key} date={key} today={key === todayKey} selected={key === value}
              items={count > 0 ? [{ id: 'n', label: `${count} available` }] : []}
              onClick={() => { onPick(key); onClose(); }} />
          );
        })}
      </div>
    </Modal>
  );
}

// The plan: /api/tal/:scriptId/suggest seeds one CAM segment per suggested cam group; the producer reshapes it freely
// (characters per segment, talent per character, add / remove segments and rows, a full calendar on the day field).
export default function SchedulePlanModal({ scriptId: scriptIdProp, initialDay, onClose, onConfirmed, card = null }) {
  const cardMode     = Boolean(card);
  const scriptId     = cardMode ? (card.scriptId || null) : scriptIdProp;
  const filmedSegs   = cardMode ? (card.segments || []).filter(sg => sg.shootStatus === 'filmed') : [];
  const editableSegs = cardMode ? (card.segments || []).filter(sg => sg.shootStatus !== 'filmed') : [];
  const isEdit       = cardMode && editableSegs.length > 0;
  const [plan,       setPlan]       = useState(null); // suggest payload
  const [loading,    setLoading]    = useState(true);
  // One entry per CAM segment: { key, camNumber, shootDay, segmentLabel, setting, noCoverage, available,
  //   talents: [{ rowKey, characterId, characterName, gender, talentId, talentName }] }
  const [segments,   setSegments]   = useState([]);
  const [allTalent,  setAllTalent]  = useState([]);   // [{ id, name, talentGender }]
  const [confirming, setConfirming] = useState(false);
  const [err,        setErr]        = useState('');
  const [calPicker,  setCalPicker]  = useState(null); // key of the segment picking a day
  // D-177 (no-guess casting): a save with an unresolved needs-casting character opens the "Casting incomplete" check.
  const [uncastIds,  setUncastIds]  = useState(() => new Set());
  const [castDialog, setCastDialog] = useState(null);   // null | [{ characterId, characterName }]
  const [serverNeeds, setServerNeeds] = useState([]);   // names from an API refusal (an old tab, a stale payload)
  const seq = useRef(0);
  const nextKey = () => `k${++seq.current}`;

  const allCharacters = useMemo(() => {
    const map = new Map();
    for (const g of (plan?.camGroups || [])) for (const c of g.characters) if (!map.has(c.id)) map.set(c.id, c);
    return [...map.values()];
  }, [plan]);

  const suggestedByCharacter = useMemo(() => {
    const map = {};
    for (const g of (plan?.camGroups || [])) {
      for (const s of (g.suggestion || [])) if (s.characterId && s.talentId) map[s.characterId] = { talentId: s.talentId, talentName: s.talentName };
    }
    return map;
  }, [plan]);

  useEffect(() => {
    api.get('/api/talent').then(r => setAllTalent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchPlan = scriptId
      ? api.get(`/api/tal/${scriptId}/suggest`).then(r => r.data)
      : Promise.resolve({ scriptId: null, scriptName: card?.title || 'Untitled production', client: card?.clientName ? { name: card.clientName } : null, camGroups: [] });
    fetchPlan
      .then(async data => {
        if (cancelled) return;
        const planData = cardMode
          ? { ...data, scriptName: card.title || data.scriptName, client: data.client || (card.clientName ? { name: card.clientName } : null) }
          : data;
        setPlan(planData);
        const emptyRow = () => ({ rowKey: nextKey(), characterId: null, characterName: null, gender: 'either', talentId: null, talentName: null });
        let seed;
        if (isEdit) {
          // Edit: rows come from the card's unfilmed segments; talent maps back to a character through the script's
          // resolved casting (suggestion entries flagged resolved).
          const charByTalent = new Map();
          for (const g of (planData.camGroups || [])) {
            for (const sg of (g.suggestion || [])) if (sg.resolved && sg.talentId && !charByTalent.has(sg.talentId)) charByTalent.set(sg.talentId, sg);
          }
          seed = editableSegs.map((sg, i) => {
            const talents = (sg.talents || []).map(t => {
              const c = t.talentId ? charByTalent.get(t.talentId) : null;
              return {
                rowKey: nextKey(),
                characterId: c?.characterId || null, characterName: c?.characterName || null, gender: c?.gender || 'either',
                talentId: t.talentId || null, talentName: t.talent?.name || t.talentDisplayName || null,
              };
            });
            return {
              key: nextKey(), camNumber: i + 1, existingId: sg.id,
              shootDay: sg.shootDay ? String(sg.shootDay).slice(0, 10) : '',
              segmentLabel: sg.segmentLabel || `Cam ${i + 1}`, setting: sg.setting || '',
              noCoverage: false, available: null,
              talents: talents.length ? talents : [emptyRow()],
            };
          });
        } else if ((planData.camGroups || []).length) {
          // initialDay pins every segment to that day; otherwise the day stays empty (scheduling is a separate act,
          // done on the Talent calendar once talent is booked).
          seed = planData.camGroups.map(g => ({
            key: nextKey(), camNumber: g.camAssignment, shootDay: initialDay || '', segmentLabel: `Cam ${g.camAssignment}`,
            setting: '', noCoverage: Boolean(g.noCoverage), available: null,
            talents: (g.suggestion || []).map(sg => ({
              rowKey: nextKey(), characterId: sg.characterId, characterName: sg.characterName, gender: sg.gender,
              talentId: sg.talentId, talentName: sg.talentName,
            })),
          }));
        } else if (cardMode) {
          seed = [{ key: nextKey(), camNumber: 1, shootDay: initialDay || '', segmentLabel: 'Cam 1', setting: '', noCoverage: false, available: null, talents: [emptyRow()] }];
        } else {
          seed = [];
        }
        setSegments(seed);

        // Availability for every seeded day, so the dropdowns and override warnings have data on first render.
        const uniqueDays = [...new Set(seed.map(sg => sg.shootDay).filter(Boolean))];
        const availByDay = {};
        await Promise.all(uniqueDays.map(async d => {
          try { availByDay[d] = (await api.get(`/api/talent-availability/by-date?date=${d}`)).data; } catch { availByDay[d] = []; }
        }));
        if (cancelled) return;
        setSegments(prev => prev.map(sg => (sg.shootDay && availByDay[sg.shootDay] ? { ...sg, available: availByDay[sg.shootDay] } : sg)));
      })
      .catch(e => setErr(e.response?.data?.error || 'The plan did not load. Close this and try again.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scriptId, initialDay]); // eslint-disable-line react-hooks/exhaustive-deps

  function patchSegment(segKey, patch) {
    setSegments(prev => prev.map(s => (s.key === segKey ? { ...s, ...patch } : s)));
  }
  function patchRow(segKey, rowKey, patch) {
    setSegments(prev => prev.map(s => (s.key !== segKey ? s : { ...s, talents: s.talents.map(t => (t.rowKey === rowKey ? { ...t, ...patch } : t)) })));
  }

  async function handleDateChange(segKey, newDate) {
    patchSegment(segKey, { shootDay: newDate, available: null });
    if (!newDate) return;
    try {
      const r = await api.get(`/api/talent-availability/by-date?date=${newDate}`);
      setSegments(prev => prev.map(s => (s.key === segKey ? { ...s, available: r.data } : s)));
    } catch { /* no warnings until a day loads */ }
  }

  function genderMatchesTalent(charGender, talGender) {
    const cg = charGender || 'either';   // untagged characters count as either (D-054)
    if (cg === 'either' || talGender === 'either') return true;
    return cg === talGender;
  }

  // Every gender-matching talent, available ones first, each flagged so an unavailable pick can warn.
  function matchOptions(seg, charGender) {
    const availIds = new Set((seg?.available || []).map(a => a.talentId));
    const matches = allTalent
      .filter(t => genderMatchesTalent(charGender, t.talentGender))
      .map(t => ({ talentId: t.id, talentName: t.name, talentGender: t.talentGender, isAvailable: availIds.has(t.id) }));
    matches.sort((a, b) => (a.isAvailable !== b.isAvailable ? (a.isAvailable ? -1 : 1) : (a.talentName || '').localeCompare(b.talentName || '')));
    return matches;
  }

  function setRowCharacter(segKey, rowKey, characterId) {
    const c = allCharacters.find(x => x.id === characterId) || null;
    const suggested = c ? suggestedByCharacter[c.id] : null;
    patchRow(segKey, rowKey, {
      characterId: c?.id || null, characterName: c?.characterName || null, gender: c?.gender || 'either',
      talentId: suggested?.talentId || null, talentName: suggested?.talentName || null,
    });
  }
  function addCharacterRow(segKey) {
    setSegments(prev => prev.map(s => (s.key !== segKey ? s : {
      ...s, talents: [...s.talents, { rowKey: nextKey(), characterId: null, characterName: null, gender: 'either', talentId: null, talentName: null }],
    })));
  }
  function removeCharacterRow(segKey, rowKey) {
    setSegments(prev => prev.map(s => (s.key !== segKey ? s : { ...s, talents: s.talents.filter(t => t.rowKey !== rowKey) })));
  }
  function addSegment() {
    setSegments(prev => {
      const nextCam = prev.length ? Math.max(...prev.map(s => s.camNumber)) + 1 : 1;
      return [...prev, {
        key: nextKey(), camNumber: nextCam, shootDay: '', segmentLabel: `Cam ${nextCam}`, setting: '', noCoverage: false, available: null,
        talents: [{ rowKey: nextKey(), characterId: null, characterName: null, gender: 'either', talentId: null, talentName: null }],
      }];
    });
  }
  function removeSegment(segKey) {
    setSegments(prev => (prev.length > 1 ? prev.filter(s => s.key !== segKey) : prev));
  }

  // available = null means not checked yet: no override warning until the day's availability lands.
  function isOverride(seg, t) {
    if (!seg?.available || !t.talentId) return false;
    return !seg.available.find(a => a.talentId === t.talentId);
  }
  const hasAnyOverride = segments.some(s => s.talents.some(t => isOverride(s, t)));
  const needsCasting = useMemo(() => {
    const map = new Map();
    for (const c of [...(plan?.needsCasting || []), ...serverNeeds]) if (c && c.characterId && !map.has(c.characterId)) map.set(c.characterId, c);
    return [...map.values()];
  }, [plan, serverNeeds]);
  const needIds = new Set(needsCasting.map(c => c.characterId));
  const rowFilled = t => Boolean(t.talentId || t.talentName);
  const pickedFor = cid => segments.some(sg => sg.talents.some(t => t.characterId === cid && rowFilled(t)));
  const unresolvedNeeds = needsCasting.filter(c => !pickedFor(c.characterId) && !uncastIds.has(c.characterId));
  const segHasTalent = sg => (cardMode ? sg.talents.some(rowFilled) : sg.talents.some(t => t.characterId && rowFilled(t)));
  // A segment whose only empty rows are needs-casting characters is not missing talent yet: the casting check resolves it.
  const segAwaitsDialog = sg => !segHasTalent(sg) && sg.talents.some(t => t.characterId && needIds.has(t.characterId) && !rowFilled(t));
  const missingDay     = segments.some(s => !s.shootDay);
  const missingTalent  = segments.some(s => !segHasTalent(s) && !segAwaitsDialog(s));
  const talentOnly = allCharacters.length === 0;

  function pickForCharacter(cid, tid) {
    const opt = allTalent.find(t => t.id === tid);
    setUncastIds(prev => { const n = new Set(prev); n.delete(cid); return n; });
    setSegments(prev => {
      const has = prev.some(sg => sg.talents.some(t => t.characterId === cid));
      if (has) return prev.map(sg => ({ ...sg, talents: sg.talents.map(t => (t.characterId === cid ? { ...t, talentId: tid || null, talentName: opt?.name || null } : t)) }));
      const c = needsCasting.find(x => x.characterId === cid);
      return prev.map((sg, i) => (i === 0 ? { ...sg, talents: [...sg.talents.filter(t => t.characterId || rowFilled(t)), { rowKey: nextKey(), characterId: cid, characterName: c?.characterName || null, gender: 'either', talentId: tid || null, talentName: opt?.name || null }] } : sg));
    });
  }
  function leaveUncast(cid) {
    setUncastIds(prev => new Set(prev).add(cid));
    setSegments(prev => prev.map(sg => ({ ...sg, talents: sg.talents.map(t => (t.characterId === cid ? { ...t, talentId: null, talentName: null } : t)) })));
  }

  async function handleConfirm() {
    if (unresolvedNeeds.length) { setCastDialog(unresolvedNeeds.map(c => ({ ...c }))); return; }
    setCastDialog(null);
    const uncastCharacterIds = [...uncastIds].filter(id => needIds.has(id) && !pickedFor(id));
    const saveSegs = segments.filter(segHasTalent);
    if (!saveSegs.length) { setErr('Nothing to plan: every character on this plan was left uncast.'); return; }
    setConfirming(true);
    setErr('');
    const onRefusal = e => {
      const d = e.response?.data;
      if (d?.code === 'casting_incomplete' && Array.isArray(d.characters)) {
        const named = d.characters.map(c => ({ characterId: c.id, characterName: c.characterName }));
        setServerNeeds(prev => [...prev, ...named]); setCastDialog(named); return true;
      }
      return false;
    };
    if (cardMode) {
      const payload = {
        uncastCharacterIds,
        camGroups: saveSegs.map(s => ({
          camAssignment: Number(s.camNumber), segmentLabel: s.segmentLabel, shootDay: s.shootDay || null, setting: s.setting || null,
          talents: s.talents.filter(t => t.talentId || t.talentName)
            .map(t => ({ characterId: t.characterId || null, talentId: t.talentId || null, talentDisplayName: t.talentId ? null : (t.talentName || null) })),
        })),
      };
      try {
        await api.put(`/api/shoot-segments/${card.id}/plan`, payload);
        onConfirmed();
      } catch (e) {
        if (!onRefusal(e)) setErr(e.response?.data?.error || 'The shoot plan did not save. Try again.');
      } finally { setConfirming(false); }
      return;
    }
    // An Approved script should have a production; a legacy one may not, so mint it (idempotent) instead of failing.
    let lpId = plan?.liveProduction?.id || null;
    if (!lpId) {
      try {
        const r = await api.post(`/api/tal/${scriptId}/ensure-live-production`);
        lpId = r.data.id;
      } catch (e) {
        setErr(e.response?.data?.error || 'The production for this script could not be created. Try again.');
        setConfirming(false);
        return;
      }
    }
    const payload = {
      liveProductionId: lpId,
      uncastCharacterIds,
      camGroups: saveSegs.map(s => ({
        camAssignment: Number(s.camNumber), segmentLabel: s.segmentLabel, shootDay: s.shootDay || null, setting: s.setting || null,
        talents: s.talents.filter(t => t.characterId && (t.talentId || t.talentName))
          .map(t => ({ characterId: t.characterId, talentId: t.talentId || null, talentDisplayName: t.talentId ? null : (t.talentName || null) })),
      })),
    };
    try {
      await api.post(`/api/tal/${scriptId}/confirm`, payload);
      onConfirmed();
    } catch (e) {
      if (!onRefusal(e)) setErr(e.response?.data?.error || 'The plan was not confirmed. Try again.');
    } finally { setConfirming(false); }
  }

  if (loading) {
    return (
      <Modal size="wide" title="Loading the suggested plan" onClose={onClose}>
        <div className="ui-stack" role="status" aria-label="Loading the suggested plan"><Skeleton width="40%" /><Skeleton variant="block" height={120} /><Skeleton variant="block" height={120} /></div>
      </Modal>
    );
  }
  if (!plan) {
    return (
      <Modal title="The plan did not load" onClose={onClose} footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
        <p className="ui-p ui-field__error" role="alert">{clean(err) || 'The plan did not load. Close this and try again.'}</p>
      </Modal>
    );
  }

  const pickerSeg = calPicker ? segments.find(s => s.key === calPicker) : null;
  const confirmLabel = hasAnyOverride ? (cardMode ? 'Save with override' : 'Confirm with override') : (cardMode ? 'Save plan' : 'Confirm plan');
  const footNote = (cardMode || plan.camGroups.length > 0) && missingTalent
    ? (cardMode ? 'Every CAM segment needs at least one talent before the plan can be saved.' : 'Every CAM segment needs at least one character with talent before the plan can be confirmed.')
    : hasAnyOverride
      ? 'One or more picks override availability. Go ahead only if that is intended.'
      : plan.camGroups.length > 0 && missingDay
        ? 'Shoot days are optional here. Book talent on the Talent calendar, then schedule from the day’s shootable scripts.'
        : '';

  return (
    <>
      <Modal size="wide" title={clean(plan.scriptName)} onClose={onClose}
        meta={[cardMode ? (isEdit ? 'Edit shoot plan' : 'Shoot plan') : 'Schedule plan', clean(plan.client?.name)].filter(Boolean).join(', ')}
        footer={(
          <>
            {footNote && (hasAnyOverride && !missingTalent
              ? <Badge tone="warn">{footNote}</Badge>
              : <span className="ui-meta">{footNote}</span>)}
            <span className="ui-spacer" />
            <Button variant="quiet" onClick={onClose}>Cancel</Button>
            <Button variant="primary" icon={Check} onClick={handleConfirm} loading={confirming}
              disabled={segments.length === 0 || missingTalent} data-testid="plan-confirm">{confirmLabel}</Button>
          </>
        )}>
        {err && <p className="ui-p ui-field__error" role="alert">{clean(err)}</p>}
        {!cardMode && plan.camGroups.length === 0 && (
          <p className="ui-p ui-text-2">This script has no character lines yet. Add them in the script editor first (untagged characters are fine: they plan as Cam 1).</p>
        )}
        {cardMode && talentOnly && (
          <p className="ui-p ui-meta" data-testid="plan-talent-only">
            {scriptId ? 'This script has no character lines. Assign talent to each CAM segment directly.' : 'Riff production. Assign talent to each CAM segment directly.'}
          </p>
        )}
        {cardMode && filmedSegs.length > 0 && (
          <section className="ui-section" data-testid="plan-filmed-kept" aria-labelledby="plan-filmed">
            <h3 className="ui-section__title" id="plan-filmed">Filmed, kept as is</h3>
            <div className="ui-region ui-list">
              {filmedSegs.map(sg => (
                <div key={sg.id} className="ui-row">
                  <Icon icon={Check} label="Filmed" />
                  <span>{clean(sg.segmentLabel)}</span>
                  <span className="ui-meta ui-num">{sg.shootDay ? String(sg.shootDay).slice(0, 10) : 'No day'}</span>
                  <span className="ui-text-2 ui-truncate">{(sg.talents || []).map(t => clean(t.talent?.name || t.talentDisplayName)).filter(Boolean).join(', ') || 'No talent'}</span>
                </div>
              ))}
            </div>
          </section>
        )}
        {segments.map(seg => (
          <section key={seg.key} className="ui-region ui-region--pad ui-stack" aria-label={`CAM ${seg.camNumber}`}>
            <div className="ui-section__head ui-section__head--center">
              <h3 className="ui-section__title">CAM {seg.camNumber}</h3>
              {seg.noCoverage && <Badge tone="warn">No matching talent available in the next 30 days</Badge>}
              {segments.length > 1 && (
                <div className="ui-section__actions">
                  <Button variant="quiet" iconOnly icon={X} label={`Remove CAM ${seg.camNumber} from the plan`} onClick={() => removeSegment(seg.key)} />
                </div>
              )}
            </div>
            <div className="ui-cols ui-cols--tight ui-cols--3">
              <div className="ui-cluster ui-cluster--nowrap">
                <DatePicker label="Shoot day" className="ui-grow" value={seg.shootDay || ''} onChange={e => handleDateChange(seg.key, e.target.value)}
                  onDoubleClick={() => setCalPicker(seg.key)} title="Double-click to pick the day from a full calendar" />
                <Button variant="quiet" iconOnly icon={CalendarDays} label={`Pick the shoot day for CAM ${seg.camNumber} from a calendar`} onClick={() => setCalPicker(seg.key)} />
              </div>
              <Input label="Segment label" value={seg.segmentLabel || ''} onChange={e => patchSegment(seg.key, { segmentLabel: e.target.value })} />
              <Input label="Setting" placeholder="Studio A, kitchen set" value={seg.setting || ''} onChange={e => patchSegment(seg.key, { setting: e.target.value })} />
            </div>
            <div className="ui-stack ui-stack--tight">
              {seg.talents.map(t => {
                const usedElsewhere = new Set(seg.talents.filter(x => x.rowKey !== t.rowKey).map(x => x.characterId).filter(Boolean));
                const charOpts = allCharacters.filter(c => !usedElsewhere.has(c.id));
                // Card mode: talent can be picked without a character; Ready mode stays character first.
                const showTalent = Boolean(t.characterId) || cardMode || talentOnly;
                const opts     = showTalent ? matchOptions(seg, t.characterId ? t.gender : 'either') : [];
                const override = isOverride(seg, t);
                return (
                  <div key={t.rowKey} className="ui-stack ui-stack--tight">
                    <div className="ui-cluster ui-cluster--nowrap">
                      {!talentOnly && (
                        <Select aria-label="Character in this CAM segment" className="ui-grow" value={t.characterId || ''}
                          onChange={e => setRowCharacter(seg.key, t.rowKey, e.target.value || null)}
                          placeholder={cardMode ? 'No character' : 'Pick a character'}
                          options={charOpts.map(c => ({ value: c.id, label: `${clean(c.characterName)} (${c.gender})` }))} />
                      )}
                      {showTalent ? (
                        <Select aria-label="Talent for this character" className="ui-grow" value={t.talentId || ''}
                          onChange={e => {
                            const tid = e.target.value || null;
                            const opt = opts.find(o => o.talentId === tid);
                            patchRow(seg.key, t.rowKey, { talentId: tid, talentName: opt?.talentName || null });
                          }}
                          placeholder="Unassigned"
                          options={opts.map(o => ({ value: o.talentId, label: `${clean(o.talentName)} (${o.talentGender})${o.isAvailable ? '' : ', not available'}` }))} />
                      ) : (
                        <span className="ui-meta ui-grow">Pick a character to choose talent</span>
                      )}
                      <Button variant="quiet" iconOnly icon={X} label="Remove this row from the segment" onClick={() => removeCharacterRow(seg.key, t.rowKey)} />
                    </div>
                    {override && (
                      <p className="ui-p ui-cluster ui-dense ui-text-danger">
                        <Icon icon={TriangleAlert} />{clean(t.talentName)} is not available on this date. Pick a highlighted day below to move the shoot.
                      </p>
                    )}
                    {override && t.talentId && (
                      <TalentAvailabilityMini talentId={t.talentId} talentName={t.talentName} baseDate={seg.shootDay} onPick={d => handleDateChange(seg.key, d)} />
                    )}
                  </div>
                );
              })}
              <span><Button variant="quiet" icon={Plus} onClick={() => addCharacterRow(seg.key)}>Add character</Button></span>
            </div>
          </section>
        ))}
        {(cardMode || plan.camGroups.length > 0) && (
          <span><Button variant="secondary" icon={Plus} onClick={addSegment}
            title="Add another CAM segment with the same fields: CAM number, shoot day, segment label, setting, characters">Add CAM segment</Button></span>
        )}
      </Modal>

      {/* D-177 (G-2): every named character is picked or left uncast before the plan saves */}
      {castDialog && (
        <Modal size="medium" testId="casting-dialog" title={<span data-testid="casting-dialog-title">Casting incomplete: these characters have no performer</span>}
          onClose={() => setCastDialog(null)}
          footer={(
            <>
              <Button variant="quiet" onClick={() => setCastDialog(null)}>Back to the plan</Button>
              <Button variant="primary" data-testid="casting-dialog-save" loading={confirming}
                disabled={castDialog.some(c => !pickedFor(c.characterId) && !uncastIds.has(c.characterId))}
                onClick={handleConfirm}>{cardMode ? 'Save plan' : 'Confirm plan'}</Button>
            </>
          )}>
          <div className="ui-region ui-list">
            {castDialog.map(c => {
              const picked = segments.flatMap(sg => sg.talents).find(t => t.characterId === c.characterId && rowFilled(t));
              const skipped = uncastIds.has(c.characterId) && !picked;
              return (
                <div key={c.characterId} className="ui-row" data-testid="casting-dialog-row" data-character={c.characterName}
                  data-state={picked ? 'picked' : skipped ? 'uncast' : 'open'}>
                  <span className="ui-truncate">{clean(c.characterName)}</span>
                  <Select aria-label={`Performer for ${clean(c.characterName)}`} data-testid="casting-dialog-pick" value={picked?.talentId || ''}
                    placeholder="Pick a performer" onChange={e => pickForCharacter(c.characterId, e.target.value || null)}
                    options={[...allTalent].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(t => ({ value: t.id, label: `${clean(t.name)}${t.talentGender ? ` (${t.talentGender})` : ''}` }))} />
                  <Button variant={skipped ? 'secondary' : 'quiet'} icon={skipped ? Check : undefined} data-testid="casting-dialog-uncast" aria-pressed={skipped}
                    onClick={() => leaveUncast(c.characterId)}>No performer, leave uncast</Button>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {pickerSeg && (
        <BigCalendarPickerModal value={pickerSeg.shootDay} title={`Shoot day for CAM ${pickerSeg.camNumber}`}
          onPick={d => handleDateChange(pickerSeg.key, d)} onClose={() => setCalPicker(null)} />
      )}
    </>
  );
}
